import { useEffect, useState } from 'react';

export interface ScriptProps {
  src: HTMLScriptElement['src'] | null;
  checkForExisting?: boolean;
  [key: string]: any;
}

type ErrorState = ErrorEvent | null;
type ScriptStatus = {
  loading: boolean;
  error: ErrorState;
  scriptEl: HTMLScriptElement;
};
type ScriptStatusMap = {
  [key: string]: ScriptStatus;
};

type ScriptAttributeKeys = keyof HTMLScriptElement;

// 이미 로딩되었거나 로딩 중인 스크립트들의 상태 저장소
export const scripts: ScriptStatusMap = {};

// 주어진 src의 <script> 태그가 이미 존재하는지 확인.
// 존재하면 scripts[src]를 갱신하고 상태를 반환하고, 없으면 undefined 반환
const checkExisting = (src: string): ScriptStatus | undefined => {
  const existing: HTMLScriptElement | null = document.querySelector(`script[src="${src}"]`);
  if (existing) {
    // 이미 존재하는 <script> 태그는 로딩이 완료되었다고 간주하고 캐시에 저장
    return (scripts[src] = {
      loading: false,
      error: null,
      scriptEl: existing,
    });
  }
  return undefined;
};

export function useScript({ src, checkForExisting = false, ...attributes }: ScriptProps): [boolean, ErrorState] {
  // 이 훅이 해당 src를 이미 처리했는지 확인
  let status: ScriptStatus | undefined = src ? scripts[src] : undefined;

  // 요청 시 기존 <script> 태그를 찾아보고, 우리가 직접 로드한 게 아니라면 상태 확인
  if (!status && checkForExisting && src && isBrowser) {
    status = checkExisting(src);
  }

  const [loading, setLoading] = useState<boolean>(status ? status.loading : Boolean(src));
  const [error, setError] = useState<ErrorState>(status ? status.error : null);
  // 중복 <script> 삽입을 방지하기 위한 로드 여부 상태
  const [scriptLoaded, setScriptLoaded] = useState<boolean>(false);

  useEffect(() => {
    // 서버 사이드이거나, src가 없거나, 이미 로드된 경우 또는 에러 상태이면 아무 작업도 안 함
    if (!isBrowser || !src || scriptLoaded || error) return;

    // 마운트 이후 src가 바뀐 경우 다시 상태 확인
    status = scripts[src];
    if (!status && checkForExisting) {
      status = checkExisting(src);
    }

    // 사용할 <script> 요소 결정 (기존 것 또는 새로 생성)
    let scriptEl: HTMLScriptElement;
    if (status) {
      scriptEl = status.scriptEl;
    } else {
      scriptEl = document.createElement('script');
      scriptEl.src = src;

      Object.entries(attributes).forEach(([key, value]) => {
        if ((key as ScriptAttributeKeys) in scriptEl) {
          (scriptEl as any)[key] = value;
        } else {
          scriptEl.setAttribute(key, value);
        }
      });

      status = scripts[src] = {
        loading: true,
        error: null,
        scriptEl: scriptEl,
      };
    }

    // 이제 status는 무조건 정의된 상태

    const handleLoad = () => {
      if (status) status.loading = false;
      setLoading(false);
      setScriptLoaded(true);
    };
    const handleError = (error: ErrorEvent) => {
      if (status) status.error = error;
      setError(error);
    };

    scriptEl.addEventListener('load', handleLoad);
    scriptEl.addEventListener('error', handleError);

    document.body.appendChild(scriptEl);

    return () => {
      scriptEl.removeEventListener('load', handleLoad);
      scriptEl.removeEventListener('error', handleError);
    };

    // attributes는 호출될 때마다 새 객체이므로 의존성 배열에 넣지 않음
  }, [src]);

  return [loading, error];
}

// 브라우저 환경인지 체크 (SSR 환경에서는 실행하지 않도록)
const isBrowser = typeof window !== 'undefined' && typeof window.document !== 'undefined';
